/**
 * Google Cloud Identity Platform through the Firebase Auth SDK
 * (@firebase/auth — not the `firebase` umbrella package, so no Firestore
 * ships). Covers (ADR-0004, ADR-0016):
 *  - Google sign-in by redirect with the Workspace `hd` restriction (the
 *    beforeSignIn blocking function in infra/ enforces it server-side too)
 *  - email/password for invite-only local accounts
 *  - TOTP second factor: sign-in challenge and first-time enrolment
 *  - ID tokens for every API call (the SDK refreshes them)
 *
 * The SDK is loaded on demand, so dev builds never download it and it stays
 * out of the main chunk.
 *
 * On native Capacitor builds (Android / iOS), Google blocks OAuth inside
 * embedded WebViews, so `signInWithGoogle()` opens the system browser at
 * `${webOrigin}/native-auth?state=sha256(verifier)` and completes sign-in
 * via either the `com.g4s.surveys://auth/google` deep link or the PKCE
 * `/api/v1/public/native-auth/exchange` endpoint, then passes the Google
 * OAuth credential to `signInWithCredential`.
 */
import type { Auth, MultiFactorResolver, TotpSecret, User } from '@firebase/auth';
import { isNative } from '../device';
import { AuthFlowError, type AuthFlowCode, type AuthProvider, type AuthSession, type MfaChallenge, type PasswordResult, type TotpEnrolment } from './types';

export interface IdentityPlatformConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  workspaceDomain: string;
  appName: string;
  apiUrl?: string;
  webOrigin?: string;
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomPkceVerifier(): string {
  const bytes = new Uint8Array(32);
  globalThis.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

export async function pkceChallengeS256(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

interface StoredG4SIdSession {
  idToken: string;
  refreshToken: string | null;
  expiresAt: number;
  email: string;
}

const G4SID_STORAGE_KEY = 'sss.auth.g4sid';
const G4SID_PKCE_KEY = 'sss.auth.g4sid.pkce';

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function openExternalUrl(url: string): void {
  if (typeof document !== 'undefined' && document.body) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function parseDeepLinkTokens(url: string): { idToken?: string; accessToken?: string; error?: string } | null {
  if (!url.startsWith('com.g4s.surveys://auth/google')) return null;
  try {
    const parsed = new URL(url);
    const hashParams = new URLSearchParams(parsed.hash.replace(/^#/, ''));
    const error = parsed.searchParams.get('error') ?? hashParams.get('error') ?? undefined;
    const idToken = hashParams.get('idToken') ?? parsed.searchParams.get('idToken') ?? undefined;
    const accessToken = hashParams.get('accessToken') ?? parsed.searchParams.get('accessToken') ?? undefined;
    if (error) return { error };
    if (idToken || accessToken) return { idToken, accessToken };
  } catch {
    // Ignore malformed URLs.
  }
  return null;
}

type AuthModule = typeof import('@firebase/auth');

/** Firebase error code (e.g. "auth/invalid-credential") → our flow code. */
export function mapFirebaseError(e: unknown): AuthFlowError {
  if (e instanceof AuthFlowError) return e;
  const code = (e as { code?: string } | null)?.code ?? '';
  const message = (e as { message?: string } | null)?.message ?? String(e);
  const map: Record<string, AuthFlowCode> = {
    'auth/invalid-credential': 'INVALID_CREDENTIALS',
    'auth/invalid-login-credentials': 'INVALID_CREDENTIALS',
    'auth/wrong-password': 'INVALID_CREDENTIALS',
    'auth/user-not-found': 'INVALID_CREDENTIALS',
    'auth/invalid-email': 'INVALID_CREDENTIALS',
    'auth/missing-password': 'INVALID_CREDENTIALS',
    'auth/too-many-requests': 'TOO_MANY_ATTEMPTS',
    'auth/invalid-verification-code': 'INVALID_CODE',
    'auth/missing-code': 'INVALID_CODE',
    'auth/code-expired': 'INVALID_CODE',
    'auth/invalid-multi-factor-session': 'INVALID_CODE',
    'auth/user-disabled': 'USER_DISABLED',
    'auth/network-request-failed': 'NETWORK',
    'auth/requires-recent-login': 'REAUTH_REQUIRED',
    'auth/user-token-expired': 'REAUTH_REQUIRED',
    'auth/operation-not-allowed': 'NOT_CONFIGURED',
    'auth/invalid-api-key': 'NOT_CONFIGURED',
    'auth/api-key-not-valid': 'NOT_CONFIGURED',
    'auth/configuration-not-found': 'NOT_CONFIGURED',
    'auth/unauthorized-domain': 'NOT_CONFIGURED',
    'auth/operation-not-supported-in-this-environment': 'UNSUPPORTED',
  };
  if (map[code]) return new AuthFlowError(map[code], message);
  // A beforeSignIn blocking function rejection surfaces as an internal error.
  if (/BLOCKING_FUNCTION/i.test(message)) return new AuthFlowError('BLOCKED', message);
  return new AuthFlowError('UNKNOWN', message);
}

function sessionOf(u: User | null): AuthSession | null {
  if (!u?.email) return null;
  const google = u.providerData.some((p) => p.providerId === 'google.com');
  return { email: u.email.toLowerCase(), method: google ? 'google' : 'password' };
}

export class IdentityPlatformAuthProvider implements AuthProvider {
  readonly mode = 'identity-platform' as const;
  private readonly listeners = new Set<() => void>();
  private loaded: Promise<{ fb: AuthModule; auth: Auth }> | null = null;
  private auth: Auth | null = null;
  private resolver: MultiFactorResolver | null = null;
  private totpSecret: TotpSecret | null = null;
  private g4sidRefreshInFlight: Promise<string | null> | null = null;
  private redirectInFlight: Promise<boolean> | null = null;

  constructor(private readonly cfg: IdentityPlatformConfig) {}

  private load(): Promise<{ fb: AuthModule; auth: Auth }> {
    this.loaded ??= (async () => {
      if (!this.cfg.apiKey || !this.cfg.authDomain || !this.cfg.projectId) {
        throw new AuthFlowError('NOT_CONFIGURED', 'VITE_IDP_API_KEY / VITE_FIREBASE_AUTH_DOMAIN / VITE_GCP_PROJECT_ID missing');
      }
      const [{ initializeApp, getApps }, fb] = await Promise.all([import('@firebase/app'), import('@firebase/auth')]);
      const app =
        getApps().find((a) => a.name === 'sss') ??
        initializeApp({ apiKey: this.cfg.apiKey, authDomain: this.cfg.authDomain, projectId: this.cfg.projectId }, 'sss');
      const auth = fb.initializeAuth(app, {
        // On iOS WKWebView (capacitor://localhost), indexedDBLocalPersistence can
        // stall initializeAuth; prefer browserLocalPersistence on native platforms.
        persistence: isNative()
          ? [fb.browserLocalPersistence, fb.indexedDBLocalPersistence]
          : [fb.indexedDBLocalPersistence, fb.browserLocalPersistence],
        // Redirect sign-in needs the browser resolver; native web views can't use it.
        popupRedirectResolver: isNative() ? undefined : fb.browserPopupRedirectResolver,
      });
      fb.onAuthStateChanged(auth, () => this.listeners.forEach((l) => l()));
      this.auth = auth;
      return { fb, auth };
    })();
    return this.loaded;
  }

  private readG4SIdSession(): StoredG4SIdSession | null {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(G4SID_STORAGE_KEY) : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredG4SIdSession;
      if (!parsed?.idToken || !parsed?.email) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private writeG4SIdSession(session: StoredG4SIdSession | null): void {
    try {
      if (typeof localStorage !== 'undefined') {
        if (session) localStorage.setItem(G4SID_STORAGE_KEY, JSON.stringify(session));
        else localStorage.removeItem(G4SID_STORAGE_KEY);
      }
    } catch {
      // Ignore storage quota errors.
    }
    this.listeners.forEach((l) => l());
  }

  private apiBase(): string {
    return (this.cfg.apiUrl || '/api/v1').replace(/\/$/, '');
  }

  current(): AuthSession | null {
    const g4s = this.readG4SIdSession();
    if (g4s) return { email: g4s.email.toLowerCase(), method: 'g4sid' };
    return sessionOf(this.auth?.currentUser ?? null);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async refreshG4SIdToken(g4s: StoredG4SIdSession): Promise<string | null> {
    try {
      const res = await fetch(`${this.apiBase()}/public/auth/zitadel/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: g4s.refreshToken }),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          idToken: string;
          refreshToken: string | null;
          expiresIn: number;
          email: string;
        };
        // If the user signed out while the refresh was in flight, do not restore the session.
        if (!this.readG4SIdSession()) return null;
        const updated: StoredG4SIdSession = {
          idToken: data.idToken,
          refreshToken: data.refreshToken ?? g4s.refreshToken,
          expiresAt: Date.now() + (data.expiresIn || 3600) * 1000,
          email: (data.email || g4s.email).toLowerCase(),
        };
        this.writeG4SIdSession(updated);
        return updated.idToken;
      }
      if (res.status === 401 || res.status === 403) {
        const latest = this.readG4SIdSession();
        if (latest && latest.idToken !== g4s.idToken && latest.expiresAt - Date.now() > 60_000) {
          return latest.idToken;
        }
        this.writeG4SIdSession(null);
        throw new AuthFlowError('REAUTH_REQUIRED', 'G4SID session expired; please sign in again');
      }
    } catch (e) {
      if (e instanceof AuthFlowError) throw e;
      // Offline with an expired token: hand back the last one so outbox retries once back online.
      return g4s.idToken;
    }
    if (g4s.expiresAt > Date.now()) return g4s.idToken;
    this.writeG4SIdSession(null);
    return null;
  }

  async getToken(force = false): Promise<string | null> {
    const g4s = this.readG4SIdSession();
    if (g4s) {
      if (!force && g4s.expiresAt - Date.now() > 60_000) {
        return g4s.idToken;
      }
      if (g4s.refreshToken) {
        this.g4sidRefreshInFlight ??= this.refreshG4SIdToken(g4s).finally(() => {
          this.g4sidRefreshInFlight = null;
        });
        return this.g4sidRefreshInFlight;
      }
      if (g4s.expiresAt > Date.now()) return g4s.idToken;
      this.writeG4SIdSession(null);
      return null;
    }

    const { auth } = await this.load();
    const u = auth.currentUser;
    if (!u) return null;
    try {
      return await u.getIdToken(force);
    } catch (e) {
      // Offline with an expired token: hand back the last one; the API call
      // fails anyway and the outbox retries once there is signal.
      if ((e as { code?: string }).code === 'auth/network-request-failed') {
        return (u as unknown as { accessToken?: string }).accessToken ?? null;
      }
      throw mapFirebaseError(e);
    }
  }

  async signInWithGoogle(opts: { loginHint?: string } = {}): Promise<void> {
    if (isNative()) {
      await this.signInWithNativeBrowser('google', opts);
      return;
    }
    const { fb, auth } = await this.load();
    const provider = new fb.GoogleAuthProvider();
    // hd "*" = any Google Workspace account (never consumer Gmail). The exact rule —
    // the Workspace domain or any subdomain (uk.g4s.com) — is enforced by the
    // beforeSignIn function and the API; a literal hd would hide subdomain accounts.
    // With a login hint Google goes straight to that account; otherwise always show
    // the chooser so people can pick a different account.
    provider.setCustomParameters(opts.loginHint ? { hd: '*', login_hint: opts.loginHint } : { hd: '*', prompt: 'select_account' });
    try {
      this.writeG4SIdSession(null);
      await fb.signInWithRedirect(auth, provider);
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async signInWithG4SId(opts: { loginHint?: string } = {}): Promise<void> {
    if (isNative()) {
      await this.signInWithNativeBrowser('g4sid', opts);
      return;
    }
    const apiBase = this.apiBase();
    let cfg: { enabled: boolean; issuerUrl: string; clientId: string; scopes: string; authorizationEndpoint: string };
    try {
      const res = await fetch(`${apiBase}/public/auth/zitadel/config`, { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      cfg = (await res.json()) as typeof cfg;
    } catch {
      throw new AuthFlowError('NETWORK', 'Could not load G4SID configuration');
    }
    if (!cfg.enabled || !cfg.issuerUrl || !cfg.clientId) {
      throw new AuthFlowError('NOT_CONFIGURED', 'G4SID authentication is not configured yet. Enable it in Admin → Settings.');
    }

    const codeVerifier = randomPkceVerifier();
    const codeChallenge = await pkceChallengeS256(codeVerifier);
    const state = `g4sid_${randomVerifier().slice(0, 32)}`;
    // Always use the web origin root (https://perimeteriq.g4s.com) so Zitadel's registered
    // Redirect URI matches regardless of which SPA route (/profile, /surveys, etc.) was open.
    const redirectUri = window.location.origin.replace(/\/+$/, '');

    try {
      sessionStorage.setItem(G4SID_PKCE_KEY, JSON.stringify({ state, codeVerifier, redirectUri }));
    } catch {
      // ignore
    }

    const authEndpoint = cfg.authorizationEndpoint || `${cfg.issuerUrl.replace(/\/+$/, '')}/oauth/v2/authorize`;
    const params = new URLSearchParams({
      client_id: cfg.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: cfg.scopes || 'openid profile email offline_access',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
    });
    if (opts.loginHint) params.set('login_hint', opts.loginHint);
    else params.set('prompt', 'select_account');

    window.location.assign(`${authEndpoint}?${params.toString()}`);
  }

  private async signInWithNativeBrowser(provider: 'google' | 'g4sid', opts: { loginHint?: string } = {}): Promise<void> {
    const verifier = randomVerifier();
    const state = await sha256Hex(verifier);
    const webOrigin = (this.cfg.webOrigin || 'https://perimeteriq.g4s.com').replace(/\/$/, '');
    const apiBase = this.apiBase();

    const params = new URLSearchParams({ state });
    if (provider === 'g4sid') params.set('provider', 'g4sid');
    if (opts.loginHint) params.set('login_hint', opts.loginHint);
    const targetUrl = `${webOrigin}/native-auth?${params.toString()}`;

    const tokens = await new Promise<{ idToken?: string; accessToken?: string }>((resolve, reject) => {
      let settled = false;
      const cleanups: Array<() => void> = [];
      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        for (const c of cleanups) {
          try {
            c();
          } catch {
            // ignore cleanup errors
          }
        }
        fn();
      };

      const pollOnce = async () => {
        if (settled) return;
        try {
          const res = await fetch(`${apiBase}/public/native-auth/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ verifier }),
          });
          if (!res.ok || settled) return;
          const data = (await res.json()) as { status?: string; idToken?: string; accessToken?: string };
          if ((data.status === 'complete' || data.status === 'completed') && (data.idToken || data.accessToken)) {
            finish(() => resolve({ idToken: data.idToken, accessToken: data.accessToken }));
          }
        } catch {
          // Network or transient error while app is backgrounded; next poll or resume will retry.
        }
      };

      const intervalId = setInterval(() => void pollOnce(), 2000);
      cleanups.push(() => clearInterval(intervalId));

      const timeoutId = setTimeout(() => {
        finish(() => reject(new AuthFlowError('UNKNOWN', `${provider === 'g4sid' ? 'G4SID' : 'Google'} sign-in timed out or was cancelled`)));
      }, 180_000);
      cleanups.push(() => clearTimeout(timeoutId));

      if (typeof document !== 'undefined') {
        const onVis = () => {
          if (document.visibilityState === 'visible') void pollOnce();
        };
        document.addEventListener('visibilitychange', onVis);
        cleanups.push(() => document.removeEventListener('visibilitychange', onVis));
      }

      void import('@capacitor/app')
        .then(({ App }) => {
          if (settled) return;
          void App.addListener('appUrlOpen', ({ url }) => {
            const parsed = parseDeepLinkTokens(url);
            if (!parsed) return;
            if (parsed.error) {
              finish(() => reject(new AuthFlowError((parsed.error as AuthFlowCode) || 'UNKNOWN', parsed.error)));
            } else {
              finish(() => resolve({ idToken: parsed.idToken, accessToken: parsed.accessToken }));
            }
          }).then((h) => cleanups.push(() => void h.remove()));

          void App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) void pollOnce();
          }).then((h) => cleanups.push(() => void h.remove()));
        })
        .catch(() => {
          // If @capacitor/app is unavailable in test environments, polling still works.
        });

      openExternalUrl(targetUrl);
    });

    const payload = tokens.idToken ? decodeJwtPayload(tokens.idToken) : null;
    if (provider === 'g4sid' || payload?.iss === 'urn:sss:g4sid') {
      if (!tokens.idToken) throw new AuthFlowError('INVALID_CREDENTIALS', 'G4SID did not return a session token');
      const email = typeof payload?.email === 'string' ? payload.email.toLowerCase() : '';
      const expSec = typeof payload?.exp === 'number' ? payload.exp : Math.floor(Date.now() / 1000) + 3600;
      if (!email) throw new AuthFlowError('INVALID_CREDENTIALS', 'G4SID token is missing email');
      if (this.loaded) {
        const { fb, auth } = await this.load();
        await fb.signOut(auth).catch(() => {});
      }
      this.writeG4SIdSession({
        idToken: tokens.idToken,
        refreshToken: tokens.accessToken ?? null,
        expiresAt: expSec * 1000,
        email,
      });
      return;
    }

    const { fb, auth } = await this.load();
    try {
      this.writeG4SIdSession(null);
      const credential = fb.GoogleAuthProvider.credential(tokens.idToken ?? null, tokens.accessToken ?? null);
      await fb.signInWithCredential(auth, credential);
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async completeNativeWebHandoff(): Promise<{ idToken?: string; accessToken?: string } | null> {
    const { fb, auth } = await this.load();
    await auth.authStateReady();
    try {
      const result = await fb.getRedirectResult(auth);
      if (!result) return null;
      const cred = fb.GoogleAuthProvider.credentialFromResult(result);
      if (!cred || (!cred.idToken && !cred.accessToken)) return null;
      return { idToken: cred.idToken ?? undefined, accessToken: cred.accessToken ?? undefined };
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async completeRedirect(): Promise<boolean> {
    this.redirectInFlight ??= this.doCompleteRedirect().finally(() => {
      this.redirectInFlight = null;
    });
    return this.redirectInFlight;
  }

  private async doCompleteRedirect(): Promise<boolean> {
    if (typeof window !== 'undefined' && !isNative() && window.location.pathname !== '/native-auth') {
      const searchParams = new URLSearchParams(window.location.search);
      const state = searchParams.get('state') ?? '';
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDesc = searchParams.get('error_description');
      if (state.startsWith('g4sid_') && !state.startsWith('g4sid_native_') && (code || error)) {
        window.history.replaceState({}, '', window.location.pathname + window.location.hash);
        const stored = (() => {
          try {
            const raw = sessionStorage.getItem(G4SID_PKCE_KEY);
            sessionStorage.removeItem(G4SID_PKCE_KEY);
            return raw ? (JSON.parse(raw) as { state?: string; codeVerifier?: string; redirectUri?: string }) : null;
          } catch {
            return null;
          }
        })();
        if (error) {
          throw new AuthFlowError('INVALID_CREDENTIALS', errorDesc || error);
        }
        if (!code || !stored?.codeVerifier || stored.state !== state) {
          throw new AuthFlowError('INVALID_CREDENTIALS', 'G4SID sign-in session expired; please try again');
        }
        const res = await fetch(`${this.apiBase()}/public/auth/zitadel/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            codeVerifier: stored.codeVerifier,
            redirectUri: stored.redirectUri || window.location.origin,
          }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          idToken?: string;
          refreshToken?: string | null;
          expiresIn?: number;
          email?: string;
          error?: { message?: string };
        };
        if (!res.ok || !body.idToken || !body.email) {
          throw new AuthFlowError('INVALID_CREDENTIALS', body.error?.message || 'G4SID token exchange failed');
        }
        if (this.loaded) {
          const { fb, auth } = await this.load();
          await fb.signOut(auth).catch(() => {});
        }
        this.writeG4SIdSession({
          idToken: body.idToken,
          refreshToken: body.refreshToken ?? null,
          expiresAt: Date.now() + (body.expiresIn || 3600) * 1000,
          email: body.email.toLowerCase(),
        });
        return true;
      }
    }

    let loaded: { fb: AuthModule; auth: Auth };
    try {
      loaded = await this.load();
    } catch (e) {
      // Not configured: nothing to complete; the login page reports it on use.
      if (e instanceof AuthFlowError && e.code === 'NOT_CONFIGURED') return false;
      throw e;
    }
    const { fb, auth } = loaded;
    await auth.authStateReady();
    if (isNative()) return false;
    try {
      return !!(await fb.getRedirectResult(auth));
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async signInWithPassword(email: string, password: string): Promise<PasswordResult> {
    const { fb, auth } = await this.load();
    try {
      this.writeG4SIdSession(null);
      await fb.signInWithEmailAndPassword(auth, email, password);
      return { status: 'signed-in' };
    } catch (e) {
      if ((e as { code?: string }).code !== 'auth/multi-factor-auth-required') throw mapFirebaseError(e);
      const resolver = fb.getMultiFactorResolver(auth, e as Parameters<AuthModule['getMultiFactorResolver']>[1]);
      const hint = resolver.hints.find((h) => h.factorId === fb.TotpMultiFactorGenerator.FACTOR_ID);
      if (!hint) throw new AuthFlowError('NOT_CONFIGURED', 'Second factor required but no authenticator app is enrolled');
      this.resolver = resolver;
      return { status: 'mfa-required', challenge: { hintUid: hint.uid, displayName: hint.displayName ?? undefined } };
    }
  }

  async verifyMfa(challenge: MfaChallenge, code: string): Promise<void> {
    const { fb } = await this.load();
    if (!this.resolver) throw new AuthFlowError('REAUTH_REQUIRED', 'No sign-in is waiting for a code');
    try {
      await this.resolver.resolveSignIn(fb.TotpMultiFactorGenerator.assertionForSignIn(challenge.hintUid, code.trim()));
      this.resolver = null;
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async startTotpEnrolment(): Promise<TotpEnrolment> {
    const { fb, auth } = await this.load();
    const u = auth.currentUser;
    if (!u) throw new AuthFlowError('REAUTH_REQUIRED', 'Not signed in');
    // Already enrolled (e.g. enrolment finished but this session predates it):
    // the API needs a token that carries the second factor — sign in again.
    if (fb.multiFactor(u).enrolledFactors.some((f) => f.factorId === fb.TotpMultiFactorGenerator.FACTOR_ID)) {
      throw new AuthFlowError('REAUTH_REQUIRED', 'Authenticator already enrolled');
    }
    try {
      const session = await fb.multiFactor(u).getSession();
      const secret = await fb.TotpMultiFactorGenerator.generateSecret(session);
      this.totpSecret = secret;
      return { secret: secret.secretKey, codeLength: secret.codeLength, otpauthUrl: secret.generateQrCodeUrl(u.email ?? undefined, this.cfg.appName) };
    } catch (e) {
      throw mapFirebaseError(e);
    }
  }

  async finishTotpEnrolment(_enrolment: TotpEnrolment, code: string): Promise<void> {
    const { fb, auth } = await this.load();
    const u = auth.currentUser;
    if (!u || !this.totpSecret) throw new AuthFlowError('REAUTH_REQUIRED', 'Enrolment expired');
    try {
      await fb.multiFactor(u).enroll(fb.TotpMultiFactorGenerator.assertionForEnrollment(this.totpSecret, code.trim()), 'Authenticator');
      this.totpSecret = null;
    } catch (e) {
      throw mapFirebaseError(e);
    }
    // The API completes enrolment when the token carries the second factor
    // (users.mfa_enrolled). If this session's refreshed token doesn't, the
    // user must sign in once more with password + code.
    const token = await u.getIdTokenResult(true);
    const factor = (token.claims.firebase as { sign_in_second_factor?: string } | undefined)?.sign_in_second_factor;
    if (!factor) throw new AuthFlowError('REAUTH_REQUIRED', 'Sign in again with the new authenticator code');
  }

  async sendPasswordReset(email: string): Promise<void> {
    const { fb, auth } = await this.load();
    try {
      await fb.sendPasswordResetEmail(auth, email);
    } catch (e) {
      // Don't reveal whether the account exists.
      if ((e as { code?: string }).code === 'auth/user-not-found') return;
      throw mapFirebaseError(e);
    }
  }

  async signOut(): Promise<void> {
    this.resolver = null;
    this.totpSecret = null;
    this.writeG4SIdSession(null);
    if (!this.loaded) return;
    const { fb, auth } = await this.load();
    await fb.signOut(auth);
  }
}
